import { FunctionComponent } from "react";
import { styled, Theme, Typography, useTheme } from "@mui/material";
import { Item } from "../../../dataAccess/api/items";
import { TypographyWithEllipsis } from "../TypographyWithEllipsis";
import EarningsBox from "../EarningsBox";
import TileLogo from "./TileLogo";
import { getTileSpacing } from ".";
import { getHeaderItemPadding } from "./ElegantTileHeader";

export interface Props {
  item: Item;
  id?: string;
}

export function useFilterTileNames(
  config: Theme["settings"]["tiles"]["titleContent"]["name"],
  listingName?: string,
  dealName?: string,
) {
  return {
    listingName: (config === "listingName" || config === "both") && listingName,
    dealName: (config === "dealName" || config === "both") && dealName,
  };
}

export const StandardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  paddingTop: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingLeft: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingRight: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
}));

export const Title = styled(TypographyWithEllipsis)(() => ({
  fontWeight: "bold",
  display: "block",
}));

export const StandardPartnerName = styled(Title)(({ theme }) => ({
  flex: "1 1 auto",
  color: theme.palette.text.tileDefault,
  marginBottom: theme.settings.tiles.spacing ? getTileSpacing(theme) : ".7rem",
  paddingLeft: getHeaderItemPadding(theme),
  paddingRight: getHeaderItemPadding(theme),
  marginTop: theme.settings.tiles.type === "elegant" ? "0.5rem" : "0",
  fontSize: theme.typography.h5.fontSize,
}));

export const StyledStandardPartnerName = styled(StandardPartnerName)(() => ({
  marginBottom: 0,
  width: "100%",
}));

export const StandardDealName = styled(Title)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
  flexBasis: theme.settings.tiles.type === "standard" ? "100%" : "auto",
  color: theme.palette.primary.main,
  width: 0,
  minWidth: "100%",
  WebkitLineClamp: `${
    theme.settings.tiles.titlesMultipleRows ? `2 !important` : `1 !important`
  }`,
  lineHeight: "1.5rem",
  maxHeight: "3rem",
  display: "-webkit-box",
  paddingLeft: getHeaderItemPadding(theme),
  paddingRight: getHeaderItemPadding(theme),
  marginTop:
    (theme.settings.tiles.type === "elegant" &&
      !theme.settings.tiles.titleContent.showVendorName) ||
    theme.settings.tiles.type === "standard"
      ? "0.5rem"
      : "0",
}));

export const StandardSavingAmount = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.light,
  flexBasis: theme.settings.tiles.type === "standard" ? "100%" : "auto",
}));

export const tileLogoMargin = "0.5rem";
const TileLogoContainer = styled("div")(() => ({
  marginRight: tileLogoMargin,
}));

const StandardTileHeader: FunctionComponent<Props> = ({
  id,
  item: { name, seller, deal },
}: Props) => {
  const theme = useTheme();
  const logo = seller.symbolLogoUrl || seller.logoUrl;
  const {
    showVendorName,
    name: tileNameConfig,
    showSavingAmount,
  } = theme.settings.tiles.titleContent;
  const { dealName, listingName } = useFilterTileNames(
    tileNameConfig,
    name,
    deal?.title,
  );
  return (
    <StandardHeader id={id}>
      {logo && (
        <TileLogoContainer>
          <TileLogo
            logoUrl={logo}
            orgName={seller.name}
            width={50}
            height={50}
          />
        </TileLogoContainer>
      )}
      {showVendorName && (
        <StyledStandardPartnerName variant="h3" noWrap title={seller.name}>
          {seller.name}
        </StyledStandardPartnerName>
      )}
      {listingName && (
        <StandardDealName
          variant={showVendorName ? "h4" : "h3"}
          title={listingName}
        >
          {listingName}
        </StandardDealName>
      )}
      {dealName && (
        <StandardDealName
          variant={showVendorName ? "h4" : "h3"}
          title={dealName}
        >
          {dealName}
        </StandardDealName>
      )}
      {showSavingAmount && !!deal?.averageTotalSavingsAmount && (
        <StandardSavingAmount>
          <EarningsBox
            color={theme.palette.tileOthersColor.main}
            averageTotalSavingsAmount={deal.averageTotalSavingsAmount}
            averageTotalSavingsPrefix={deal.averageTotalSavingsPrefix}
            averageTotalSavingsCurrency={deal.averageTotalSavingsCurrency}
          />
        </StandardSavingAmount>
      )}
    </StandardHeader>
  );
};

export default StandardTileHeader;
